import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import PublishModal from '../components/PublishModal';

// =====================================================
// WRITER'S STUDIO - Story creation and editing
// =====================================================

const STORY_TYPES = [
  { id: 'original', name: 'Original', icon: '📖', description: 'Your own original story' },
  { id: 'fan_fiction', name: 'Fan Fiction', icon: '🎭', description: 'Based on existing works' },
  { id: 'poetry', name: 'Poetry', icon: '🌸', description: 'Poems and verses' },
  { id: 'essay', name: 'Essay', icon: '📝', description: 'Non-fiction writing' },
  { id: 'screenplay', name: 'Screenplay', icon: '🎬', description: 'Scripts and dialogues' },
];

const GENRES = [
  'Fantasy', 'Sci-Fi', 'Romance', 'Mystery', 'Horror', 'Thriller',
  'Drama', 'Comedy', 'Adventure', 'Historical', 'Contemporary', 'Slice of Life',
  'Action', 'Supernatural', 'Dystopian', 'Cyberpunk', 'Steampunk', 'Urban Fantasy',
];

const MATURITY_RATINGS = [
  { id: 'everyone', name: 'Everyone', icon: '🌟', description: 'Suitable for all ages' },
  { id: 'teen', name: 'Teen', icon: '🔷', description: 'May contain mild content' },
  { id: 'mature', name: 'Mature', icon: '🔶', description: 'Adult themes, no explicit content' },
];

export default function WritersStudio() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  // Story metadata
  const [storyType, setStoryType] = useState(STORY_TYPES[0]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [genre, setGenre] = useState('');
  const [subGenres, setSubGenres] = useState([]);
  const [maturityRating, setMaturityRating] = useState(MATURITY_RATINGS[0]);
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [coverImage, setCoverImage] = useState(null);
  
  // Fan fiction specific
  const [fandom, setFandom] = useState('');
  const [characters, setCharacters] = useState([]);
  const [characterInput, setCharacterInput] = useState('');
  
  // Content
  const [chapters, setChapters] = useState([{
    id: 1,
    title: 'Chapter 1',
    content: '',
    wordCount: 0,
    status: 'draft',
  }]);
  const [activeChapter, setActiveChapter] = useState(0);
  
  // UI state
  const [saving, setSaving] = useState(false);
  const [showMetadataPanel, setShowMetadataPanel] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [autoSaveTimer, setAutoSaveTimer] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  
  // Word count calculation
  useEffect(() => {
    const updatedChapters = chapters.map(ch => ({
      ...ch,
      wordCount: ch.content.trim() ? ch.content.trim().split(/\s+/).length : 0,
    }));
    if (JSON.stringify(updatedChapters) !== JSON.stringify(chapters)) {
      setChapters(updatedChapters);
    }
  }, [chapters.map(c => c.content).join('')]);
  
  const totalWordCount = chapters.reduce((sum, ch) => sum + ch.wordCount, 0);
  
  // Add tag
  const addTag = () => {
    if (tagInput.trim() && tags.length < 20) {
      setTags([...tags, tagInput.trim().toLowerCase()]);
      setTagInput('');
    }
  };
  
  // Add character
  const addCharacter = () => {
    if (characterInput.trim() && characters.length < 20) {
      setCharacters([...characters, characterInput.trim()]);
      setCharacterInput('');
    }
  };
  
  // Add chapter
  const addChapter = () => {
    const newChapter = {
      id: Date.now(),
      title: `Chapter ${chapters.length + 1}`,
      content: '',
      wordCount: 0,
      status: 'draft',
    };
    setChapters([...chapters, newChapter]);
    setActiveChapter(chapters.length);
  };
  
  // Update chapter content
  const updateChapterContent = (content) => {
    const updated = [...chapters];
    updated[activeChapter] = {
      ...updated[activeChapter],
      content,
      wordCount: content.trim() ? content.trim().split(/\s+/).length : 0,
    };
    setChapters(updated);
  };
  
  // Update chapter title
  const updateChapterTitle = (newTitle) => {
    const updated = [...chapters];
    updated[activeChapter] = { ...updated[activeChapter], title: newTitle };
    setChapters(updated);
  };
  
  // Delete chapter
  const deleteChapter = (index) => {
    if (chapters.length === 1) return;
    const updated = chapters.filter((_, i) => i !== index);
    setChapters(updated);
    setActiveChapter(Math.min(activeChapter, updated.length - 1));
  };
  
  // Save story
  const saveStory = async (publish = false) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (!title.trim()) {
      alert('Please enter a title for your story');
      return;
    }
    
    setSaving(true);
    try {
      const storyData = {
        title,
        description,
        story_type: storyType.id,
        genre,
        sub_genres: subGenres,
        maturity_rating: maturityRating.id,
        tags,
        fandom: storyType.id === 'fan_fiction' ? fandom : null,
        characters: storyType.id === 'fan_fiction' ? characters : [],
        chapters: chapters.map(ch => ({
          title: ch.title,
          content: ch.content,
          status: publish ? 'published' : ch.status,
        })),
        status: publish ? 'published' : 'draft',
        visibility: publish ? 'public' : 'private',
      };
      
      const response = await apiService.request('/api/stories', {
        method: 'POST',
        body: JSON.stringify(storyData),
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (response.story) {
        setLastSaved(new Date());
        if (publish) {
          alert('Story published successfully!');
          navigate(`/stories/${response.story.id}`);
        }
      }
      
      return response.story;
    } catch (error) {
      console.error('Failed to save:', error);
      alert('Failed to save story. Please try again.');
      throw error;
    } finally {
      setSaving(false);
    }
  };
  
  // Publish with cross-media support
  const handlePublish = async (publishData, mediaData) => {
    setSaving(true);
    try {
      // First save the story
      const storyData = {
        title: publishData.title || title,
        description: publishData.description || description,
        story_type: storyType.id,
        genre: genre,
        sub_genres: subGenres,
        maturity_rating: maturityRating.id,
        tags: publishData.tags || tags,
        fandom: storyType.id === 'fan_fiction' ? fandom : null,
        characters: storyType.id === 'fan_fiction' ? characters : [],
        chapters: chapters.map(ch => ({
          title: ch.title,
          content: ch.content,
          status: 'published',
        })),
        status: 'published',
        visibility: publishData.visibility,
        community_id: publishData.community_id,
      };
      
      const response = await apiService.request('/api/stories', {
        method: 'POST',
        body: JSON.stringify(storyData),
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (response.story) {
        // Create a post to the stories feed
        if (publishData.visibility === 'public') {
          try {
            await apiService.createCrossMediaPost({
              caption: publishData.description || title,
              hashtags: publishData.tags || tags,
              visibility: publishData.visibility,
              content_type: 'document',
              feed_type: 'stories',
              source_type: 'writers_studio',
              story_id: response.story.id,
              community_id: publishData.community_id,
              linked_content: publishData.linked_content || [],
              media_metadata: {
                word_count: chapters.reduce((sum, ch) => sum + ch.wordCount, 0),
                chapter_count: chapters.length,
                genre: genre,
                story_type: storyType.id,
              },
            });
          } catch (postError) {
            console.warn('Feed post creation failed, but story was saved:', postError);
          }
        }
        
        navigate(`/stories/${response.story.id}`);
      }
    } catch (error) {
      console.error('Failed to publish:', error);
      throw error;
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="h-full bg-slate-950 flex flex-col pt-16 pb-20">
      {/* Toolbar */}
      <div className="flex-none bg-slate-900/80 backdrop-blur border-b border-white/10 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center gap-4 flex-wrap">
          {/* Back button */}
          <button
            onClick={() => navigate('/upload')}
            className="p-2 rounded-lg hover:bg-white/10 transition"
          >
            ← Back
          </button>
          
          {/* Story type indicator */}
          <div className="flex items-center gap-2 px-3 py-1 bg-white/10 rounded-lg">
            <span>{storyType.icon}</span>
            <span className="text-sm">{storyType.name}</span>
          </div>
          
          {/* Word count */}
          <div className="text-sm text-slate-400">
            {totalWordCount.toLocaleString()} words
          </div>
          
          {/* Toggle panels */}
          <button
            onClick={() => setShowMetadataPanel(!showMetadataPanel)}
            className={`p-2 rounded-lg transition ${showMetadataPanel ? 'bg-amber-500/30' : 'hover:bg-white/10'}`}
            title="Story Details"
          >
            📋
          </button>
          
          <button
            onClick={() => setShowPreview(!showPreview)}
            className={`p-2 rounded-lg transition ${showPreview ? 'bg-emerald-500/30' : 'hover:bg-white/10'}`}
            title="Preview"
          >
            👁️
          </button>
          
          <div className="flex-1" />
          
          {/* Save status */}
          {lastSaved && (
            <span className="text-xs text-slate-500">
              Saved {lastSaved.toLocaleTimeString()}
            </span>
          )}
          
          {/* Actions */}
          <button
            onClick={() => saveStory(false)}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-medium transition"
          >
            {saving ? 'Saving...' : 'Save Draft'}
          </button>
          <button
            onClick={() => setShowPublishModal(true)}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-sm font-bold transition hover:scale-105"
          >
            Publish
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Metadata panel */}
        {showMetadataPanel && (
          <div className="w-80 flex-none bg-slate-900/50 border-r border-white/10 p-4 overflow-y-auto">
            <h2 className="text-lg font-bold mb-4">Story Details</h2>
            
            {/* Story Type */}
            <div className="mb-4">
              <label className="text-xs text-slate-400 block mb-2">Type</label>
              <div className="grid grid-cols-2 gap-2">
                {STORY_TYPES.map(type => (
                  <button
                    key={type.id}
                    onClick={() => setStoryType(type)}
                    className={`p-2 rounded-lg text-left transition ${
                      storyType.id === type.id
                        ? 'bg-amber-500/30 border border-amber-400/50'
                        : 'bg-white/5 hover:bg-white/10'
                    }`}
                  >
                    <div className="text-xl">{type.icon}</div>
                    <div className="text-xs font-medium">{type.name}</div>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Title */}
            <div className="mb-4">
              <label className="text-xs text-slate-400 block mb-1">Title *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Your story's title"
                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2"
              />
            </div>
            
            {/* Description */}
            <div className="mb-4">
              <label className="text-xs text-slate-400 block mb-1">Synopsis</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What's your story about?"
                rows={4}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 resize-none"
              />
            </div>
            
            {/* Genre */}
            <div className="mb-4">
              <label className="text-xs text-slate-400 block mb-1">Primary Genre</label>
              <select
                value={genre}
                onChange={(e) => setGenre(e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2"
              >
                <option value="">Select genre...</option>
                {GENRES.map(g => (
                  <option key={g} value={g.toLowerCase()}>{g}</option>
                ))}
              </select>
            </div>
            
            {/* Maturity Rating */}
            <div className="mb-4">
              <label className="text-xs text-slate-400 block mb-2">Rating</label>
              <div className="flex gap-2">
                {MATURITY_RATINGS.map(rating => (
                  <button
                    key={rating.id}
                    onClick={() => setMaturityRating(rating)}
                    className={`flex-1 p-2 rounded-lg text-center transition ${
                      maturityRating.id === rating.id
                        ? 'bg-amber-500/30 border border-amber-400/50'
                        : 'bg-white/5 hover:bg-white/10'
                    }`}
                  >
                    <div>{rating.icon}</div>
                    <div className="text-xs">{rating.name}</div>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Fan Fiction Fields */}
            {storyType.id === 'fan_fiction' && (
              <>
                <div className="mb-4">
                  <label className="text-xs text-slate-400 block mb-1">Fandom</label>
                  <input
                    type="text"
                    value={fandom}
                    onChange={(e) => setFandom(e.target.value)}
                    placeholder="e.g., Harry Potter, Star Wars"
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2"
                  />
                </div>
                
                <div className="mb-4">
                  <label className="text-xs text-slate-400 block mb-1">Characters</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={characterInput}
                      onChange={(e) => setCharacterInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addCharacter()}
                      placeholder="Add character"
                      className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2"
                    />
                    <button onClick={addCharacter} className="px-3 bg-white/10 rounded-lg">+</button>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {characters.map((char, i) => (
                      <span key={i} className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded text-xs flex items-center gap-1">
                        {char}
                        <button onClick={() => setCharacters(characters.filter((_, j) => j !== i))}>×</button>
                      </span>
                    ))}
                  </div>
                </div>
              </>
            )}
            
            {/* Tags */}
            <div className="mb-4">
              <label className="text-xs text-slate-400 block mb-1">Tags ({tags.length}/20)</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addTag()}
                  placeholder="Add tag"
                  className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2"
                />
                <button onClick={addTag} className="px-3 bg-white/10 rounded-lg">+</button>
              </div>
              <div className="flex flex-wrap gap-1">
                {tags.map((tag, i) => (
                  <span key={i} className="px-2 py-1 bg-amber-500/20 text-amber-300 rounded text-xs flex items-center gap-1">
                    #{tag}
                    <button onClick={() => setTags(tags.filter((_, j) => j !== i))}>×</button>
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Chapter list */}
        <div className="w-48 flex-none bg-slate-900/30 border-r border-white/10 p-2 overflow-y-auto">
          <div className="flex items-center justify-between mb-3 px-2">
            <span className="text-xs text-slate-400">Chapters</span>
            <button
              onClick={addChapter}
              className="p-1 rounded hover:bg-white/10 text-sm"
              title="Add Chapter"
            >
              +
            </button>
          </div>
          
          {chapters.map((chapter, index) => (
            <div
              key={chapter.id}
              onClick={() => setActiveChapter(index)}
              className={`p-2 rounded-lg mb-1 cursor-pointer transition group ${
                activeChapter === index
                  ? 'bg-amber-500/30 border border-amber-400/50'
                  : 'hover:bg-white/10'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium truncate flex-1">{chapter.title}</span>
                {chapters.length > 1 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteChapter(index);
                    }}
                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-white/20 rounded text-xs"
                  >
                    🗑️
                  </button>
                )}
              </div>
              <div className="text-xs text-slate-500">{chapter.wordCount} words</div>
            </div>
          ))}
        </div>

        {/* Editor */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Chapter title */}
          <div className="flex-none p-4 border-b border-white/10">
            <input
              type="text"
              value={chapters[activeChapter]?.title || ''}
              onChange={(e) => updateChapterTitle(e.target.value)}
              className="w-full bg-transparent text-2xl font-bold outline-none placeholder-slate-500"
              placeholder="Chapter Title"
            />
          </div>
          
          {/* Content area */}
          <div className="flex-1 overflow-y-auto p-4">
            {showPreview ? (
              <div className="prose prose-invert max-w-none">
                <div className="whitespace-pre-wrap font-serif text-lg leading-relaxed">
                  {chapters[activeChapter]?.content || 'Start writing to see preview...'}
                </div>
              </div>
            ) : (
              <textarea
                value={chapters[activeChapter]?.content || ''}
                onChange={(e) => updateChapterContent(e.target.value)}
                placeholder="Start writing your story here..."
                className="w-full h-full bg-transparent resize-none outline-none text-lg leading-relaxed font-serif placeholder-slate-500"
              />
            )}
          </div>
          
          {/* Status bar */}
          <div className="flex-none px-4 py-2 border-t border-white/10 flex items-center gap-4 text-xs text-slate-500">
            <span>{chapters[activeChapter]?.wordCount || 0} words in chapter</span>
            <span>{totalWordCount.toLocaleString()} total words</span>
            <span>Chapter {activeChapter + 1} of {chapters.length}</span>
          </div>
        </div>
      </div>
      
      {/* Publish Modal */}
      <PublishModal
        isOpen={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        onPublish={handlePublish}
        contentType="story"
        initialTitle={title}
        initialDescription={description}
        initialTags={tags}
      />
    </div>
  );
}
